/** @jsx jsx */
import { jsx } from '@emotion/core';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import compose from 'lodash/fp/compose';
import defaultsDeep from 'lodash/defaultsDeep';
import Cookies from 'universal-cookie';
import { object, string } from 'yup';
import { Formik } from 'formik';
import Lottie from 'react-lottie';
import { withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import { withNamespaces } from 'react-i18next';
import searchAnimation from '../../../assets/animations/search-load.json';
import Spacer from '../../ui/Spacer';
import { Container, Column, Row } from '../../../styles/Grid';
import { SafePureComponent } from '../../../utils/SafePureComponent';
import DotComponent from '../../ui/DotComponent';
import Select, { mapDropdownValues } from '../../ui/Form/Dropdown';
import { Input, Checkbox, PhoneNumber } from '../../ui/Form/index';
import CtaLink from '../../ui/CtaLink';
import getComponent from '../../../utils/getComponent';
import { COMPONENT_NAMES } from '../../../utils/constants';
import { getSolutionPrefilledValues } from '../Enquiry';
import { getPrefilledSolutionValue } from '../../../utils/getPrefilledSolutionValue';
import {
  validatePhoneNumberWithIso,
  postEnquiryData,
} from '../../../services/enquiry';
import { setDataLayerObj } from '../../../utils/analytics';
import * as dict from '../../../utils/dictionarykeys';
import * as styles from './index.styles';

export const defaultData = {
  AntiForgery: {
    name: '',
    value: '',
  },
  DomainCountryISOCode2: {
    value: '',
  },
  LocationID: {
    value: '',
  },
  PhoneNumberOptions: [
    {
      fields: {
        CountryName: {
          value: '',
        },
        DialingCode: {
          value: '',
        },
        Flag: {
          value: '',
        },
        CountryISOCode3: {
          value: '',
        },
        CountryISOCode2: {
          value: '',
        },
      },
    },
  ],
  PrivacyPolicyLink: {
    value: {
      href: '#',
      linktype: 'internal',
      text: 'Link',
      anchor: '',
      querystring: '',
      target: '',
      id: '',
    },
  },
  SolutionsDropdownOptions: [],
};

const FORM_ID = 'contact_form';
const RESET_FORM_TIME = 8000;

const animationOptions = {
  loop: true,
  autoplay: true,
  animationData: searchAnimation,
};

const addRequiredMark = placeholder => `${placeholder} *`;

const getEnquiryFields = sitecoreContext => {
  const component = getComponent(sitecoreContext, COMPONENT_NAMES.enquiryModal);
  const fields = get(component, 'fields', {});
  return defaultsDeep(fields, defaultData);
};

const getInitialState = () => ({
  showError: false,
  isLoading: false,
  formSent: false,
  resetKey: 0,
});

export class InlineForm extends SafePureComponent {
  cookies = new Cookies();
  channelCookie = this.cookies.get('channel_attrib') || 'Organic';
  countryData = null;

  validationSchema = this.createValidationSchema();
  fields = getEnquiryFields(this.props.sitecoreContext);
  initialValues = this.getInitialFormValues();
  state = getInitialState();

  createValidationSchema() {
    const { t } = this.props;
    return object().shape({
      Solution: string().required(t(dict.FORM_COPY.REQUIRED_OPTION)),
      FullName: string().required(t(dict.FORM_COPY.REQUIRED_FIELD)),
      Email: string()
        .email(t(dict.FORM_COPY.INVALID_EMAIL))
        .required(t(dict.FORM_COPY.REQUIRED_FIELD)),
      PhoneNumber: string()
        .required(t(dict.FORM_COPY.REQUIRED_FIELD))
        .matches('^[0-9]*$', t(dict.FORM_COPY.INVALID_PHONE)),
    });
  }

  sendData = async (values, actions) => {
    this.setState({ isLoading: true });
    this.resetGeneralError(actions);
    const [error, isValid] = await this.validatePhoneNumber(values);

    if (!isValid) {
      this.handlePhoneNumberError(actions, error);
      return;
    }

    const formSent = await this.sendEnquiryData(values, actions);

    if (formSent) {
      this.setState({ formSent: true });
      this.resetForm();
    }
  };

  validatePhoneNumber = values => {
    const phoneNumber = values.PhoneNumber;
    const { CountryISOCode2: countryISOCode2 } = this.countryData;
    return validatePhoneNumberWithIso({ countryISOCode2, phoneNumber });
  };

  handlePhoneNumberError = (actions, error) => {
    const { t } = this.props;
    actions.setErrors({ PhoneNumber: t(dict.FORM_COPY.INVALID_PHONE) });
    this.trackFormSubmitError(error);
    this.setState({ showError: true, isLoading: false });
  };

  sendEnquiryData = async (values, actions) => {
    const { fields } = this;
    const mappedValues = this.mapValues(values, this.fields);
    const { Solution: solution } = mappedValues;
    const [error, data] = await postEnquiryData(
      mappedValues,
      fields.AntiForgery,
    );

    if (error) {
      this.handleEnquiryDataError(actions, error);
      return false;
    }

    this.trackFormSubmitSuccess(data, solution);

    return true;
  };

  handleEnquiryDataError = (actions, error) => {
    const { t } = this.props;
    actions.setStatus({ msg: t(dict.FORM_COPY.GENERAL_ERROR) });
    this.trackFormSubmitError(error);
    this.setState({ isLoading: false });
  };

  resetGeneralError = actions => {
    actions.setStatus({ msg: '' });
  };

  mapValues = (values, { DomainCountryISOCode2, LocationID }) => {
    return {
      ...values,
      ...this.countryData,
      Solution: get(values, 'Solution.value', values.Solution),
      ChannelAttrib: this.channelCookie,
      DomainCountryISOCode2: DomainCountryISOCode2.value,
      LocationID: LocationID.value,
    };
  };

  trackFormSubmitSuccess(data, solution) {
    const { id, createDate, requestedTourDate } = data;
    setDataLayerObj({
      id,
      createDate,
      requestedTourDate,
      event: 'contactFormResponseObject',
      formName: 'enquiry',
      formType: 'inline form',
      solutionType: solution,
      submitButton: 'submit',
    });
  }

  trackFormSubmitError(error) {
    setDataLayerObj({
      event: 'contactFormFailedSubmission',
      contactFormError: error.message,
      formId: FORM_ID,
    });
  }

  setCountryData = countryData => {
    this.countryData = countryData;
  };

  getInitialFormValues() {
    const { SolutionsDropdownOptions } = this.fields;
    const defaultFields = {
      Solution: getPrefilledSolutionValue(this.props.sitecoreContext),
    };
    const prefilledSolution = getSolutionPrefilledValues(
      defaultFields,
      SolutionsDropdownOptions,
    );

    return {
      Solution: prefilledSolution,
      FullName: '',
      Email: '',
      PhoneNumber: '',
      PrivacyPolicy: true,
      SendMessages: false,
    };
  }

  resetForm = () => {
    setTimeout(() => {
      this.setState({ ...getInitialState(), resetKey: Math.random() });
    }, this.props.resetTime);
  };

  /**
   * Needed since the dictionary key is optional. When the key is empty, `t` returns the `key` again.
   */
  hasSubHeadline = () => {
    const { t } = this.props;
    return t(dict.inlineForm.subheadline) !== dict.inlineForm.subheadline;
  };

  renderGeneralError(status) {
    const message = get(status, 'msg');
    if (message) {
      return (
        <div css={styles.notificationContainer}>
          <div css={[styles.notification, styles.notificationError]}>
            <i className="icon-error" css={styles.badge} />
            <span>{message}</span>
          </div>
        </div>
      );
    }
  }

  renderPrivacyPolicyLinkLabel = () => {
    const { t } = this.props;
    const { PrivacyPolicyLink } = this.fields;
    return (
      <div css={styles.privacyContainer}>
        <span css={styles.privacyLabel}>
          {t(dict.inlineForm.privacyPolicy)}
        </span>
        <CtaLink css={styles.privacyPolicyLink} cta={PrivacyPolicyLink} />
      </div>
    );
  };

  renderThankYouMessage = () => {
    const { t } = this.props;
    if (this.state.formSent) {
      return (
        <div css={styles.notificationContainer}>
          <div css={styles.notification}>
            <i
              className="icon-success"
              css={[styles.badge, styles.badgeSuccess]}
            />
            <span>{t(dict.inlineForm.successMessage)}</span>
          </div>
        </div>
      );
    }
  };

  renderSubmitButton() {
    const { t } = this.props;
    const { isLoading, formSent } = this.state;

    if (formSent) {
      return (
        <button css={[styles.submitButton, styles.submitButtonSent]} disabled>
          <i css={styles.submitButtonIcon} className="icon-check" />
          {t(dict.inlineForm.submitSuccess)}
        </button>
      );
    }

    if (isLoading) {
      return (
        <button
          css={[styles.submitButton, styles.submitButtonLoading]}
          disabled
        >
          <Lottie
            options={animationOptions}
            isClickToPauseDisabled
            height={40}
          />
        </button>
      );
    }

    return (
      <button type="submit" css={styles.submitButton}>
        {t(dict.inlineForm.submit)}
      </button>
    );
  }

  safeRender() {
    const { t } = this.props;
    const { resetKey, isLoading, formSent } = this.state;
    const { fields, initialValues, validationSchema } = this;
    const {
      SolutionsDropdownOptions,
      PhoneNumberOptions,
      DomainCountryISOCode2,
    } = fields;
    const fieldDisabled = isLoading || formSent;

    return (
      <Formik
        key={resetKey}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={this.sendData}
        render={({
          errors,
          touched,
          handleChange,
          handleSubmit,
          status,
          values,
        }) => (
          <Spacer
            backgroundColor={this.props.fields.BackgroundColor}
            marginCss={styles.containerMargin}
            paddingCss={styles.containerPadding}
          >
            <form onSubmit={handleSubmit}>
              <Container>
                <Row>
                  <Column cols={{ sm: 6, md: 8, lg: 8 }} offsets={{ lg: 2 }}>
                    <h2 css={styles.headline}>
                      {t(dict.inlineForm.headline)}
                      <DotComponent />
                    </h2>
                    {this.hasSubHeadline() && (
                      <p css={styles.subheadline}>
                        {t(dict.inlineForm.subheadline)}
                      </p>
                    )}
                    <Row>
                      <Column cols={{ sm: 6, md: 4 }}>
                        {!isEmpty(SolutionsDropdownOptions) && (
                          <Select
                            selectProps={{
                              'data-testid': 'solution-dropdown',
                              disabled: fieldDisabled,
                            }}
                            css={styles.inputCommon}
                            {...styles.selectCssProps}
                            id="Solution"
                            disablePlaceholder
                            options={mapDropdownValues(
                              SolutionsDropdownOptions,
                              true,
                            )}
                            placeholder={addRequiredMark(
                              t(dict.inlineForm.placeholders.solution),
                            )}
                            onChangeProp={handleChange}
                            error={errors.Solution && touched.Solution}
                            errorMessage={errors.Solution}
                            defaultValue={values.Solution.value}
                            defaultLabel={values.Solution.label}
                          />
                        )}
                      </Column>
                      <Column cols={{ sm: 6, md: 4 }}>
                        <Input
                          id="FullName"
                          css={styles.inputCommon}
                          {...styles.inputCssProps}
                          placeholder={addRequiredMark(
                            t(dict.inlineForm.placeholders.fullName),
                          )}
                          onChange={handleChange}
                          error={errors.FullName && touched.FullName}
                          errorMessage={errors.FullName}
                          value={values.FullName}
                          inputProps={{ disabled: fieldDisabled }}
                        />
                      </Column>
                    </Row>
                    <Row>
                      <Column cols={{ sm: 6, md: 4 }}>
                        <Input
                          id="Email"
                          css={styles.inputCommon}
                          {...styles.inputCssProps}
                          placeholder={addRequiredMark(
                            t(dict.inlineForm.placeholders.email),
                          )}
                          type="email"
                          onChange={handleChange}
                          error={errors.Email && touched.Email}
                          errorMessage={errors.Email}
                          value={values.Email}
                          inputProps={{ disabled: fieldDisabled }}
                        />
                      </Column>
                      <Column cols={{ sm: 6, md: 4 }}>
                        <PhoneNumber
                          options={PhoneNumberOptions}
                          customCss={styles.inputCommon}
                          innerCss={styles.phoneNumberFieldCssProps}
                          id="PhoneNumber"
                          defaultValue={DomainCountryISOCode2.value}
                          placeholder={addRequiredMark(
                            t(dict.inlineForm.placeholders.phoneNumber),
                          )}
                          onInit={({ countryData }) =>
                            this.setCountryData(countryData)
                          }
                          onChange={handleChange}
                          onChangeProp={(_event, countryData) =>
                            this.setCountryData(countryData)
                          }
                          error={errors.PhoneNumber && touched.PhoneNumber}
                          errorMessage={errors.PhoneNumber}
                          value={values.PhoneNumber}
                          inputProps={{
                            'data-testid': 'phone-input',
                            disabled: fieldDisabled,
                          }}
                          selectProps={{
                            'data-testid': 'phone-dropdown',
                            disabled: fieldDisabled,
                          }}
                        />
                      </Column>
                    </Row>
                    <Checkbox
                      css={styles.checkboxCss}
                      id="SendMessages"
                      onChangeProp={handleChange}
                      label={t(dict.inlineForm.placeholders.sendMessages)}
                      disabled={fieldDisabled}
                      inputProps={{
                        'data-testid': 'send-messages-checkbox',
                      }}
                    />
                    <Row>
                      <Column cols={{ sm: 6, md: 4, lg: [4, 8], xl: [3, 8] }}>
                        {this.renderSubmitButton()}
                      </Column>
                    </Row>
                    {this.renderGeneralError(status)}
                    {this.renderThankYouMessage()}
                    {this.renderPrivacyPolicyLinkLabel()}
                  </Column>
                </Row>
              </Container>
            </form>
          </Spacer>
        )}
      />
    );
  }
}

InlineForm.displayName = 'InlineForm';

InlineForm.defaultProps = {
  fields: {},
  t: key => key,
  resetTime: RESET_FORM_TIME,
};

InlineForm.propTypes = {
  fields: PropTypes.any,
  resetTime: PropTypes.number,
};

export default compose(withNamespaces(), withSitecoreContext())(InlineForm);
